import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { WorkSpaceIcon } from 'components/SVG';
import { InfoCard, SparkDataTable } from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import { SparkImageLoader } from 'sharedComponents/UI';
import { useGetCategories } from 'hooks/Categories';
import SparkMainAppSearchFilters from 'components/common/SparkMainAppSearchFilters';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContext';
import Pagination from 'sharedComponents/Pagination';
import { useWorkspaceSearch } from 'hooks/Workspace/useWorkspaceSearch';
import WorkSpaceSkeleton from 'sharedComponents/Skeleton/WorkSpaceSkeleton';

type WorkspacesRow = {
  id: string;
  name: string;
  type: string;
  imageURL: string;
};

function WorkspaceListSearch() {
  const [page, setPage] = React.useState(1);

  const navigate = useNavigate();
  const { categoryIdMap } = useGetCategories(null);
  const {
    filter: { search: query },
  } = useSparkSearchContext();
  const { workspaces = [], meta, isLoading } = useWorkspaceSearch(query, page);

  const columns: Column<WorkspacesRow>[] = [
    {
      field: 'name',
      headerText: 'Name',
      filterAble: true,
      sortable: true,
      cellRenderer: (row) => (
        <Box display='flex' alignItems='center' gap={2} flex={1}>
          {row.imageURL ? (
            <SparkImageLoader
              src={row.imageURL}
              alt={row.imageURL}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
              }}
            />
          ) : (
            <WorkSpaceIcon width='40' height='40' />
          )}
          <Typography>{row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerText: 'Type',
      filterAble: true,
      sortable: true,
      cellRenderer: (row) => (
        <Box display='flex' alignItems='center' gap={2} flex={1}>
          <Typography>{row.type}</Typography>
        </Box>
      ),
    },
  ];

  const rows: WorkspacesRow[] = workspaces.map((workspace) => {
    return {
      id: workspace.id,
      name: workspace.attributes.name,
      type:
        categoryIdMap?.[workspace.attributes.categoryID || ''].attributes
          .name || '',
      imageURL: workspace.attributes.imageURL || '',
    };
  });

  return (
    <Grid container gap={2} direction={'column'}>
      <SparkMainAppSearchFilters
        hideCategories={true}
        placeholder='Search Workspaces'
        onChange={() => setPage(1)}
      />
      {query && (
        <Typography
          fontFamily='Mont-Semibold'
          variant='h6'
          marginLeft={0.5}
        >{`${rows.length} ${
          rows.length === 1 ? 'result' : 'results'
        } for ${query}`}</Typography>
      )}
      {isLoading ? (
        <WorkSpaceSkeleton />
      ) : (
        <>
          {!!rows.length ? (
            <>
              <SparkDataTable<WorkspacesRow>
                columns={columns}
                rows={rows || []}
                isRowClickable={true}
                pagination={false}
                onRowClick={(rowID) =>
                  navigate(`/settings/workspaces/${rowID}`)
                }
                showLeftChevron={false}
              />
              {rows.length >= 5 && (
                <Pagination
                  currentPage={page}
                  setCurrentPage={setPage}
                  dataCount={meta?.total || 0}
                />
              )}
            </>
          ) : (
            <Box>
              <InfoCard info={'No Workspaces found'} type='error' />
            </Box>
          )}
        </>
      )}
    </Grid>
  );
}

export default WorkspaceListSearch;
